export type Vec3 = { x: number; y: number; z: number };
export type Projected = { x: number; y: number; visible: boolean; depth: number };

const DEG = Math.PI / 180;

export function lonLatToXYZ(lonDeg: number, latDeg: number): Vec3 {
  // Convention: at theta=0, lon=0 (Greenwich) faces the viewer (+Z), east is
  // on the right (+X), north is up (+Y). Previous convention put lon=0 on the
  // right edge and lon=90 at the centre — which mirror-flipped east/west.
  const lon = lonDeg * DEG;
  const lat = latDeg * DEG;
  const cosLat = Math.cos(lat);
  return {
    x: cosLat * Math.sin(lon),
    y: Math.sin(lat),
    z: cosLat * Math.cos(lon)
  };
}

export function rotateY(p: Vec3, theta: number): Vec3 {
  const c = Math.cos(theta), s = Math.sin(theta);
  return { x: c * p.x + s * p.z, y: p.y, z: -s * p.x + c * p.z };
}

export function rotateX(p: Vec3, phi: number): Vec3 {
  const c = Math.cos(phi), s = Math.sin(phi);
  return { x: p.x, y: c * p.y - s * p.z, z: s * p.y + c * p.z };
}

export function project(p: Vec3, radius: number, cx: number, cy: number): Projected {
  return {
    x: cx + p.x * radius,
    y: cy - p.y * radius,
    visible: p.z >= 0,
    depth: p.z
  };
}

/** Great-circle as a quadratic Bézier whose control point is lifted off the chord toward the sphere surface. */
export function arcPath(a: Vec3, b: Vec3, radius = 100, cx = 0, cy = 0, lift = 1.25): string {
  const ax = cx + a.x * radius;
  const ay = cy - a.y * radius;
  const bx = cx + b.x * radius;
  const by = cy - b.y * radius;
  const mx = ((a.x + b.x) / 2) * lift;
  const my = ((a.y + b.y) / 2) * lift;
  const cxp = cx + mx * radius;
  const cyp = cy - my * radius;
  return `M ${ax.toFixed(2)} ${ay.toFixed(2)} Q ${cxp.toFixed(2)} ${cyp.toFixed(2)} ${bx.toFixed(2)} ${by.toFixed(2)}`;
}

/** Golden-spiral evenly-distributed points on sphere — for graticule. */
export function goldenSpiralPoints(n: number): Vec3[] {
  const points: Vec3[] = [];
  const phi = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const theta = phi * i;
    points.push({ x: Math.cos(theta) * r, y, z: Math.sin(theta) * r });
  }
  return points;
}
